import { date } from '@/lib/date';

export interface PrintEvent {
  description: string;
  startDate: string | Date;
  endDate: string | Date;
}

export interface PrintCouple {
  husbandName?: string;
  husbandPhone?: string;
  wifeName?: string;
  wifePhone?: string;
  email?: string | null;
  cep?: string;
  address?: string;
  addressNumber?: string | null;
  addressComplement?: string | null;
  baptismStatus?: string;
  confirmationStatus?: string;
  marriageSacrament?: boolean;
  marriageDate?: string | Date | null;
  parish?: string | null;
  pastoralMovement?: string | null;
  childrenInfo?: string | null;
}

const RED = '#A3262B';
const LOCATION = "Jardim da Auxiliadora, Santa Bárbara d'Oeste - SP";
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

const esc = (v: unknown) =>
  String(v ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!,
  );
const pad = (n: number) => String(n).padStart(2, '0');

const dateBR = (v?: string | Date | null) => (v ? date(v).format('DD/MM/YYYY') : '');

const phoneBR = (v?: string) => {
  const d = (v ?? '').replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v ?? '';
};

const cepBR = (v?: string) => {
  const d = (v ?? '').replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : (v ?? '');
};

export const rangeLong = (ev?: PrintEvent) => {
  if (!ev) return '';
  const s = date(ev.startDate);
  const e = date(ev.endDate);
  const [d1, m1, d2, m2, y2] = [s.date(), s.month(), e.date(), e.month(), e.year()];
  return m1 === m2
    ? `${pad(d1)} e ${pad(d2)} de ${MONTHS[m2]} de ${y2}`
    : `${pad(d1)} de ${MONTHS[m1]} a ${pad(d2)} de ${MONTHS[m2]} de ${y2}`;
};

const css = `
  @page{size:A4;margin:0}
  *{box-sizing:border-box}
  html,body{margin:0;padding:0}
  body{background:#E9E9E6;font-family:'Inter',system-ui,sans-serif;color:#1C1C1F;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .page{width:210mm;height:297mm;margin:24px auto;background:#fff;box-shadow:0 6px 28px rgba(0,0,0,.14);padding:14mm 15mm 12mm;display:flex;flex-direction:column;overflow:hidden;position:relative}
  .disp{font-family:'Plus Jakarta Sans','Inter',sans-serif}
  .hd{display:flex;align-items:center;justify-content:space-between;gap:12mm;padding-bottom:5mm;border-bottom:1.5pt solid #1C1C1F}
  .brand{display:flex;align-items:center;gap:4mm}
  .brand img{height:15mm;width:auto;filter:brightness(0)}
  .brand .t1{font-weight:800;font-size:17pt;letter-spacing:-.02em;line-height:1}
  .brand .t2{font-size:7.5pt;font-weight:700;letter-spacing:.14em;color:#6B6B70;margin-top:1.5mm}
  .eyebrow{font-size:7.5pt;font-weight:700;letter-spacing:.12em;color:${RED}}
  .doc{text-align:right;display:flex;flex-direction:column;gap:1.2mm}
  .doc .title{font-weight:800;font-size:13pt;letter-spacing:-.01em}
  .doc .meta{font-size:8.5pt;color:#4A4A4F}
  .sec{margin-top:6mm;display:flex;flex-direction:column;gap:3.2mm}
  .sec h3{margin:0;display:flex;align-items:baseline;gap:3mm;font-size:10.5pt;font-weight:800;padding-bottom:1.6mm;border-bottom:.6pt solid #CFCFCC}
  .sec h3 span{font-size:7.5pt;letter-spacing:.12em;color:${RED};font-weight:700}
  .row{display:grid;gap:5mm}
  .f{display:flex;flex-direction:column;gap:1mm;min-width:0}
  .f label{font-size:7pt;font-weight:700;letter-spacing:.09em;color:#6B6B70;text-transform:uppercase}
  .f .v{min-height:7mm;border-bottom:.6pt solid #8A8A8E;font-size:10.5pt;padding:1mm 0 .8mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .opts{display:flex;flex-wrap:wrap;gap:6mm;align-items:center;min-height:7mm}
  .opt{display:flex;align-items:center;gap:2mm;font-size:10pt}
  .box{width:4mm;height:4mm;border:.8pt solid #1C1C1F;border-radius:.8mm;display:inline-flex;align-items:center;justify-content:center}
  .box.on{background:#1C1C1F}
  .box.on::after{content:'';width:1.4mm;height:2.4mm;border:solid #fff;border-width:0 .5mm .5mm 0;transform:rotate(45deg) translate(-.2mm,-.3mm)}
  .sign{margin-top:auto;display:grid;grid-template-columns:1fr 1fr .6fr;gap:8mm;padding-top:10mm}
  .sign .f .v{min-height:11mm}
  .ft{display:flex;justify-content:space-between;font-size:7.5pt;color:#6B6B70;padding-top:4mm;margin-top:5mm;border-top:.6pt solid #CFCFCC}
  .note{font-size:8.5pt;line-height:1.45;color:#4A4A4F;border:.6pt dashed #A8A8A8;border-radius:2mm;padding:3mm 4mm;margin-top:5mm}
  table{width:100%;border-collapse:collapse;margin-top:5mm;font-size:8.8pt}
  th{text-align:left;font-size:6.8pt;font-weight:700;letter-spacing:.1em;color:#6B6B70;padding:2mm 2mm;border-bottom:1pt solid #1C1C1F;text-transform:uppercase}
  td{padding:2.1mm 2mm;border-bottom:.5pt solid #DADAD6;vertical-align:middle}
  td.n{color:#6B6B70;width:8mm;font-variant-numeric:tabular-nums}
  td .a{font-weight:600}
  td .b{color:#4A4A4F;font-size:8pt;margin-top:.5mm}
  .tag{display:inline-block;font-size:6.8pt;font-weight:700;border:.6pt solid #1C1C1F;border-radius:1mm;padding:.3mm 1.2mm;margin-right:1mm}
  .tag.off{border-color:#BDBDBA;color:#A8A8A8}
  .stats{display:flex;gap:8mm;margin-top:5mm}
  .stat{display:flex;flex-direction:column;gap:.6mm}
  .stat b{font-family:'Plus Jakarta Sans',sans-serif;font-size:15pt;font-weight:800}
  .stat span{font-size:7pt;letter-spacing:.1em;color:#6B6B70;font-weight:700}
  @media print{body{background:#fff}.page{margin:0;box-shadow:none;break-after:page}.page:last-child{break-after:auto}}
`;

const shell = (
  title: string,
  body: string,
) => `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>${esc(title)}</title>
<base href="${esc(typeof window !== 'undefined' ? `${window.location.origin}/` : '/')}">
<link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@700;800&display=swap" rel="stylesheet">
<style>${css}</style></head><body>${body}</body></html>`;

const header = (
  docTitle: string,
  meta: { eyebrow?: string; line?: string },
  logo: string,
) => `<div class="hd">
    <div class="brand"><img src="${esc(logo)}" alt=""><div><div class="t1 disp">Enchei-vos</div><div class="t2">RETIRO DE CASAIS • COMUNIDADE CATÓLICA APÓSTOLOS</div></div></div>
    <div class="doc"><span class="eyebrow">${esc(meta.eyebrow)}</span><span class="title disp">${esc(docTitle)}</span><span class="meta">${esc(meta.line)}</span></div>
  </div>`;

const field = (label: string, value?: string | null) =>
  `<div class="f"><label>${esc(label)}</label><div class="v">${esc(value)}</div></div>`;

const opts = (label: string, list: [unknown, string][], value: unknown) =>
  `<div class="f"><label>${esc(label)}</label><div class="opts">${list
    .map(
      ([v, l]) =>
        `<span class="opt"><span class="box${value === v ? ' on' : ''}"></span>${esc(l)}</span>`,
    )
    .join('')}</div></div>`;

const SAC: [string, string][] = [
  ['BOTH', 'Ambos'],
  ['ONLY_ONE', 'Somente um'],
  ['NONE', 'Nenhum'],
];

export function renderFicha(
  event?: PrintEvent,
  couple?: PrintCouple,
  logo = '/assets/logo.png',
) {
  const c = couple ?? {};
  const has = !!couple;
  const body = `<section class="page">
      ${header('Ficha de Inscrição', { eyebrow: event ? event.description.toUpperCase() : 'RETIRO DE CASAIS', line: event ? `${rangeLong(event)} • ${LOCATION}` : '' }, logo)}
      <div class="sec"><h3><span>01</span>O casal</h3>
        <div class="row" style="grid-template-columns:1.6fr 1fr">${field('Nome do esposo', c.husbandName)}${field('Telefone do esposo', phoneBR(c.husbandPhone))}</div>
        <div class="row" style="grid-template-columns:1.6fr 1fr">${field('Nome da esposa', c.wifeName)}${field('Telefone da esposa', phoneBR(c.wifePhone))}</div>
        <div class="row" style="grid-template-columns:1fr">${field('E-mail', c.email)}</div>
      </div>
      <div class="sec"><h3><span>02</span>Endereço</h3>
        <div class="row" style="grid-template-columns:.7fr 2fr .6fr">${field('CEP', cepBR(c.cep))}${field('Endereço', c.address)}${field('Número', c.addressNumber)}</div>
        <div class="row" style="grid-template-columns:1fr">${field('Complemento', c.addressComplement)}</div>
      </div>
      <div class="sec"><h3><span>03</span>Vida de fé</h3>
        <div class="row" style="grid-template-columns:1fr 1fr">${opts('Situação do Batismo', SAC, c.baptismStatus)}${opts('Situação da Crisma', SAC, c.confirmationStatus)}</div>
        <div class="row" style="grid-template-columns:1fr 1fr">${opts(
          'São casados na Igreja?',
          [
            [true, 'Sim'],
            [false, 'Ainda não'],
          ],
          has ? c.marriageSacrament : undefined,
        )}${field('Data do matrimônio', dateBR(c.marriageDate))}</div>
        <div class="row" style="grid-template-columns:1fr 1fr">${field('Paróquia', c.parish)}${field('Pastoral / Movimento', c.pastoralMovement)}</div>
      </div>
      <div class="sec"><h3><span>04</span>Família</h3>
        <div class="row" style="grid-template-columns:1fr">${field('Filhos (quantidade / idades)', c.childrenInfo)}</div>
      </div>
      <div class="note">Os dados informados são usados apenas pela equipe do retiro para a organização do encontro. Inscrições também podem ser feitas em casaisencheivos.com.br.</div>
      <div class="sign">${field('Assinatura do esposo')}${field('Assinatura da esposa')}${field('Data')}</div>
      <div class="ft"><span>Comunidade Católica Apóstolos</span><span>casaisencheivos.com.br</span></div>
    </section>`;
  return shell('Ficha de inscrição — Enchei-vos', body);
}

export function renderCoupleList(
  event: PrintEvent,
  couples: PrintCouple[] = [],
  logo = '/assets/logo.png',
  perPage = 18,
) {
  const pages: PrintCouple[][] = [];
  for (let i = 0; i < Math.max(1, couples.length); i += perPage)
    pages.push(couples.slice(i, i + perPage));
  const married = couples.filter((c) => c.marriageSacrament).length;
  const tg = (on: boolean, l: string) =>
    `<span class="tag${on ? '' : ' off'}">${l}</span>`;

  const body = pages
    .map(
      (rows, pi) => `<section class="page">
      ${header('Lista de Casais', { eyebrow: event.description.toUpperCase(), line: rangeLong(event) }, logo)}
      ${
        pi === 0
          ? `<div class="stats">
        <div class="stat"><b>${couples.length}</b><span>CASAIS</span></div>
        <div class="stat"><b>${couples.length * 2}</b><span>PARTICIPANTES</span></div>
        <div class="stat"><b>${married}</b><span>CASADOS NA IGREJA</span></div>
      </div>`
          : ''
      }
      <table><thead><tr><th>#</th><th>Casal</th><th>Telefones</th><th>Paróquia</th><th>Sacram.</th><th style="width:16mm">Presença</th></tr></thead><tbody>
      ${
        rows.length
          ? rows
              .map(
                (c, i) => `<tr>
        <td class="n">${pad(pi * perPage + i + 1)}</td>
        <td><div class="a">${esc(c.husbandName)}</div><div class="b">${esc(c.wifeName)}</div></td>
        <td><div>${esc(phoneBR(c.husbandPhone))}</div><div class="b">${esc(phoneBR(c.wifePhone))}</div></td>
        <td>${esc(c.parish || '—')}</td>
        <td style="white-space:nowrap">${tg(c.baptismStatus === 'BOTH', 'B')}${tg(c.confirmationStatus === 'BOTH', 'C')}${tg(!!c.marriageSacrament, 'M')}</td>
        <td><span class="box"></span></td></tr>`,
              )
              .join('')
          : '<tr><td colspan="6" style="padding:8mm 2mm;color:#6B6B70">Nenhum casal inscrito neste encontro.</td></tr>'
      }
      </tbody></table>
      <div class="ft" style="margin-top:auto"><span>B: Batismo dos dois • C: Crisma dos dois • M: Casados na Igreja</span><span>Página ${pi + 1} de ${pages.length}</span></div>
    </section>`,
    )
    .join('');
  return shell(`Lista de casais — ${event.description}`, body);
}
